import { useState, useEffect } from "react";
import { CloudArrowUp, ArrowClockwise, Trash, Clock } from "@phosphor-icons/react";
import { fmtBRL } from "../lib/format";
import { getPendingOrders, removePendingOrder } from "../lib/offlineOrders";

const orderTotal = (payload) => {
  const items = payload.items || [];
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.quant) || 0) * (parseFloat(it.unit_price) || 0), 0);
  return Math.max(subtotal - (parseFloat(payload.discount) || 0), 0);
};

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "agora há pouco";
  if (mins < 60) return `há ${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
};

export const PendingOrdersPanel = ({ onSync }) => {
  const [items, setItems] = useState(getPendingOrders());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const update = () => setItems(getPendingOrders());
    window.addEventListener("pending-orders-changed", update);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("pending-orders-changed", update);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  if (items.length === 0) return null;

  const trySync = async () => {
    if (!onSync) return;
    setSyncing(true);
    await onSync();
    setSyncing(false);
  };

  const discard = (item) => {
    if (window.confirm(`Descartar o pedido offline de ${item.client_name || "cliente"} (${fmtBRL(orderTotal(item.payload))})?\n\nEle NÃO será enviado ao servidor.`)) {
      removePendingOrder(item.localId);
    }
  };

  return (
    <div data-testid="pending-orders-panel"
      className="mb-4 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-amber-400/15">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-400">
          <CloudArrowUp size={16} weight="bold" data-testid="pending-orders-badge" />
          {items.length} pedido{items.length !== 1 ? "s" : ""} aguardando envio
        </span>
        <button data-testid="pending-sync-now-button" onClick={trySync} disabled={syncing || !navigator.onLine}
          title={navigator.onLine ? "Tentar enviar agora" : "Sem conexão — aguardando internet"}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-200 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 px-3 py-1.5 rounded-lg transition-colors">
          <ArrowClockwise size={13} className={syncing ? "animate-spin" : ""} />
          {syncing ? "Enviando..." : "Enviar agora"}
        </button>
      </div>
      <ul className="divide-y divide-amber-400/10">
        {items.map((it) => (
          <li key={it.localId} data-testid={`pending-order-${it.localId}`}
            className="flex items-center justify-between gap-3 px-4 py-2.5">
            <div className="min-w-0">
              <p className="text-sm text-zinc-200 truncate">{it.client_name || "Cliente"}</p>
              <p className="text-xs text-zinc-500 flex items-center gap-1">
                <Clock size={11} /> {timeAgo(it.created_at)}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-sm font-semibold text-emerald-400">{fmtBRL(orderTotal(it.payload))}</span>
              <button data-testid={`pending-discard-${it.localId}`} onClick={() => discard(it)}
                title="Descartar este pedido offline"
                className="text-zinc-500 hover:text-rose-400 p-1 transition-colors"><Trash size={14} /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
